import { Content } from "@iventis/translations/content/typed-content";
import React, { FunctionComponent, ReactNode, useMemo } from "react";
import { Node } from "@iventis/domain-model/model/node";
import { Body1, Header2 } from "@iventis/styles/src/components/texts";
import { Theme } from "@emotion/react";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { styled, inlineTextIconMargin, sectionalMargin, sidebarPadding } from "@iventis/styles";

/** Icons shown next to the title of the hint relating to these node types children */
const getNodeIcon = (node: Node): IconProp => {
    switch (node.type) {
        case NodeType.MappingLibrary:
        case NodeType.PersonalMappingLibrary:
        case NodeType.ScheduleVersion:
            return ["far", "pen-ruler"];
        case NodeType.Folder:
            return ["fas", "folder"];
        case NodeType.Map:
            return ["far", "layer-group"];
        default:
            throw new Error(`Node type has no matching preview icon: ${node.type}`);
    }
};

export const NodeEmptyComponent: FunctionComponent<{
    node: Node;
    typeName?: string;
    image?: ReactNode;
    callToAction?: ReactNode;
    testId?: string;
}> = ({ node, typeName, image, callToAction, testId }) => {
    const nodeType = node.type as keyof typeof Content.library.hints;
    const translate = useIventisTranslate();
    const title = useMemo(() => translate(Content.library.hints[nodeType].title, { type: typeName ?? translate(nodeType) }), [node.id]);
    const text = useMemo(
        () =>
            // Loop through each paragraph
            Object.values(Content.library.hints[nodeType])
                // Ignore the first one, as it's the title
                .slice(1)
                // eslint-disable-next-line iventis/no-uncontained-conditional-text
                .map((text) => <Body1 key={text}>{translate(text, { type: typeName ?? translate(node.type) })}</Body1>),
        [node.id]
    );
    return (
        <StyledNodeEmptyContainer data-cy="node-empty" data-testid={testId}>
            <figure className="empty-layer-graphic">{image}</figure>
            <StyledHeader>
                <span style={{ marginRight: inlineTextIconMargin }}>
                    <FontAwesomeIcon size="sm" icon={getNodeIcon(node)} />
                </span>
                {title}
            </StyledHeader>
            <TextContainer>{text}</TextContainer>
            {callToAction}
        </StyledNodeEmptyContainer>
    );
};

export const StyledNodeEmptyContainer = styled.div`
    width: 100%;
    padding: ${sidebarPadding};
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    color: ${(props: { theme: Theme }) => props.theme.typographyColors.moreSubdued};
    text-align: center;
    flex-grow: 1;
    gap: ${sectionalMargin};
    overflow-y: auto;
    > * {
        max-width: 450px;
    }
    .empty-layer-graphic svg {
        min-height: 150px;
    }
`;

export const StyledHeader = styled(Header2)`
    font-size: larger;
    font-weight: 500;
`;

export const TextContainer = styled.div`
    max-width: 75%;
`;
